import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Building2, Users, Link2, ScrollText, Search, Trash2, Crown, Key, Activity,
  UserPlus, Unlink, Mic, Music, Trophy, Tv, Bell,
} from 'lucide-react';
import { toast } from 'sonner';
import ax from '@/lib/apiClient';

const SUBSCRIPTION_LABELS = {
  active: 'Active', comped: 'Active (complimentary)', past_due: 'Payment past due',
  canceled: 'Canceled', inactive: 'Not subscribed', paused: 'Paused',
};
const SUBSCRIPTION_HELP = {
  inactive: 'Your program license is verified, but the Command Center subscription is not active yet. Your data is read-only until it is.',
  canceled: 'Your subscription has ended. Your data stays available to view and export. Your program keeps working on its own.',
  past_due: 'Your last payment did not go through. Update it to keep your account fully active.',
  paused: 'Your account is paused. Contact support to resume it.',
};


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ════════════════════════════════════════════════════════════════════
   ADMIN DASHBOARD
   ════════════════════════════════════════════════════════════════════ */
function useTabFromHeader(valid) {
  // The header links ("Accounts", "Audit Log", "Programs", "Hosts") navigate here with { state: { tab } }.
  // Clicking the in-page tabs writes the same state back, so the highlighted header link always matches.
  const location = useLocation();
  const navigate = useNavigate();
  const fromUrl = valid.includes(location.state?.tab) ? location.state.tab : 'overview';
  const [tab, setTabState] = useState(fromUrl);
  useEffect(() => { setTabState(fromUrl); }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const setTab = (next) => {
    setTabState(next);
    navigate(location.pathname, { replace: true, state: { tab: next } });
  };
  return [tab, setTab];
}

function AdminDashboard() {
  const [tab, setTab] = useTabFromHeader(['overview']);
  const [stats, setStats] = useState(null);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => { try { const { data } = await ax.get(`${API}/admin/stats`); setStats(data); } catch {} };

  const statCards = [
    { label: 'Total Accounts', value: stats?.total_accounts ?? 0, icon: Building2 },
    { label: 'Active Subs', value: stats?.active_subscriptions ?? 0, icon: Crown },
    { label: 'Programs', value: stats?.linked_programs ?? 0, icon: Link2 },
    { label: 'Total Users', value: stats?.total_users ?? 0, icon: Users },
  ];

  return (
    <div>
      <div className="mb-8">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#fbdd68]/80 mb-1.5">Command Center</p>
        <h1 data-testid="admin-dashboard-title" className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Master Admin</h1>
        <p className="text-sm text-[#8892b0] mt-1">Platform overview across every account</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-[#141b50]/80 border border-[rgba(251,221,104,0.15)] p-1 rounded-xl">
          <TabsTrigger data-testid="admin-tab-overview" value="overview" className="data-[state=active]:bg-[#fbdd68]/10 data-[state=active]:text-[#fbdd68] text-[#8892b0]">
            <Activity className="w-4 h-4 mr-1.5" /> Overview
          </TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {statCards.map((c, i) => (
              <Card key={c.label} className={`glass-tile rounded-2xl border-0 animate-fade-in-up stagger-${i + 1}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-[#8892b0]">{c.label}</p>
                      <p data-testid={`admin-stat-${c.label.toLowerCase().replace(/\s/g, '-')}`} className="text-3xl font-extrabold text-white mt-1 font-outfit">{c.value}</p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-[#fbdd68]/10 border border-[#fbdd68]/20 flex items-center justify-center shrink-0">
                      <c.icon className="w-5 h-5 text-[#fbdd68]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   OWNER DASHBOARD
   ════════════════════════════════════════════════════════════════════ */
function OrgNotices() {
  // Small card, no popups: "a member left" and other notices for THIS owner's org only.
  const [notices, setNotices] = useState([]);
  const [unread, setUnread] = useState(0);

  const fetchNotices = async () => {
    try { const { data } = await ax.get(`${API}/account/notices?limit=10`); setNotices(data.notices); setUnread(data.unread); } catch {}
  };
  useEffect(() => { fetchNotices(); }, []);

  const markRead = async (id) => {
    try { await ax.post(`${API}/account/notices/${id}/read`); fetchNotices(); } catch {}
  };

  const unreadNotices = notices.filter((n) => !n.read_at);
  if (unreadNotices.length === 0) return null;
  return (
    <Card className="glass-panel rounded-2xl border-0 mb-4 animate-fade-in-up" data-testid="org-notices-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-4 h-4 text-[#fbdd68]" />
          <p className="text-sm font-semibold text-white">Notices</p>
          <Badge data-testid="org-notices-unread" className="bg-[#fbdd68]/10 text-[#fbdd68] border-[#fbdd68]/30">{unread}</Badge>
        </div>
        <div className="space-y-2">
          {unreadNotices.map((n) => (
            <div key={n.id} data-testid={`org-notice-${n.id}`}
              className="flex items-start gap-3 p-3 rounded-lg bg-[rgba(20,27,80,0.4)] border border-[rgba(251,221,104,0.08)]">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white">{n.text}</p>
                <p className="text-[10px] text-[#8892b0]/60 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
              </div>
              <Button size="sm" variant="ghost" data-testid={`org-notice-read-${n.id}`} onClick={() => markRead(n.id)}
                className="text-[#8892b0] hover:text-white shrink-0">Mark read</Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function OwnerDashboard() {
  const [tab, setTab] = useTabFromHeader(['overview', 'programs', 'hosts']);
  const [account, setAccount] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [linkCode, setLinkCode] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [addHostOpen, setAddHostOpen] = useState(false);
  const [hostForm, setHostForm] = useState({ email: '', name: '', password: '' });
  const [hostLoading, setHostLoading] = useState(false);

  useEffect(() => { fetchAccount(); fetchPrograms(); fetchHosts(); }, []);

  const fetchAccount = async () => { try { const { data } = await ax.get(`${API}/account/me`); setAccount(data.account); } catch {} };
  const fetchPrograms = async () => { try { const { data } = await ax.get(`${API}/account/programs`); setPrograms(data.programs); } catch {} };
  const fetchHosts = async () => { try { const { data } = await ax.get(`${API}/schedule/employees`); setHosts(data); } catch {} };

  const handleLinkProgram = async (e) => {
    e.preventDefault(); setLinkLoading(true);
    try { const { data } = await ax.post(`${API}/account/programs/link`, { code: linkCode }); toast.success('Program linked!'); setLinkCode(''); fetchPrograms(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to link program'); }
    finally { setLinkLoading(false); }
  };

  const handleUnlink = async (pid) => {
    try { await ax.delete(`${API}/account/programs/${pid}`); toast.success('Program unlinked'); fetchPrograms(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddHost = async (e) => {
    e.preventDefault(); setHostLoading(true);
    try { await ax.post(`${API}/account/hosts`, hostForm); toast.success('Host added!'); setHostForm({ email: '', name: '', password: '' }); setAddHostOpen(false); fetchHosts(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to add host'); }
    finally { setHostLoading(false); }
  };

  const handleRemoveHost = async (h) => {
    const msg = h.linked
      ? `Delete ${h.name}? Their cloud login is removed too, along with their venue roles and blackout dates.`
      : `Delete ${h.name}? Their venue roles and blackout dates are removed too.`;
    if (!window.confirm(msg)) return;
    try { await ax.delete(`${API}/schedule/employees/${h.id}`); toast.success('Host removed'); fetchHosts(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const activePrograms = programs.filter(p => p.status === 'active');

  return (
    <div>
      <div className="mb-8">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#fbdd68]/80 mb-1.5">Command Center</p>
        <h1 data-testid="owner-dashboard-title" className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">My Account</h1>
        <p className="text-sm text-[#8892b0] mt-1">Manage your programs and hosts</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-[#141b50]/80 border border-[rgba(251,221,104,0.15)] p-1 rounded-xl">
          <TabsTrigger data-testid="owner-tab-overview" value="overview" className="data-[state=active]:bg-[#fbdd68]/10 data-[state=active]:text-[#fbdd68] text-[#8892b0]">Overview</TabsTrigger>
          <TabsTrigger data-testid="owner-tab-programs" value="programs" className="data-[state=active]:bg-[#fbdd68]/10 data-[state=active]:text-[#fbdd68] text-[#8892b0]">
            <Link2 className="w-4 h-4 mr-1.5" /> Programs
          </TabsTrigger>
          <TabsTrigger data-testid="owner-tab-hosts" value="hosts" className="data-[state=active]:bg-[#fbdd68]/10 data-[state=active]:text-[#fbdd68] text-[#8892b0]">
            <Users className="w-4 h-4 mr-1.5" /> Hosts
          </TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <OrgNotices />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="glass-panel rounded-2xl border-0 animate-fade-in-up">
              <CardContent className="p-5">
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#8892b0] mb-2">Subscription</p>
                <Badge data-testid="owner-subscription-status" className={['active', 'comped'].includes(account?.subscription_status) ? 'bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30' : account?.subscription_status === 'past_due' ? 'bg-[#fbdd68]/10 text-[#fbdd68] border-[#fbdd68]/30' : 'bg-[#ef4444]/10 text-[#ef4444] border-[#ef4444]/30'}>
                  {SUBSCRIPTION_LABELS[account?.subscription_status] || account?.subscription_status || 'Loading...'}
                </Badge>
                <p className="text-xs text-[#8892b0] mt-2">$4.99/month</p>
                {SUBSCRIPTION_HELP[account?.subscription_status] && (
                  <p className="text-xs text-[#8892b0] mt-2" data-testid="subscription-help">{SUBSCRIPTION_HELP[account?.subscription_status]}</p>
                )}
              </CardContent>
            </Card>
            <Card className="glass-panel rounded-2xl border-0 animate-fade-in-up stagger-1">
              <CardContent className="p-5">
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#8892b0] mb-2">Linked Programs</p>
                <p className="text-3xl font-extrabold text-white">{activePrograms.length}</p>
                <p className="text-xs text-[#8892b0] mt-1">of {account?.max_programs ?? 5} max</p>
              </CardContent>
            </Card>
            <Card className="glass-panel rounded-2xl border-0 animate-fade-in-up stagger-2">
              <CardContent className="p-5">
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#8892b0] mb-2">Active Hosts</p>
                <p className="text-3xl font-extrabold text-white">{hosts.filter(h => h.active !== false).length}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Programs */}
        <TabsContent value="programs">
          <Card className="glass-panel rounded-2xl border-0">
            <CardHeader>
              <CardTitle className="text-white text-lg">Link a Program</CardTitle>
              <CardDescription className="text-[#8892b0]">Enter the 6-character code from your BIG Hat Entertainment program</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLinkProgram} className="flex gap-3 mb-6">
                <Input data-testid="owner-link-program-input" placeholder="AB3XY7" value={linkCode}
                  onChange={(e) => setLinkCode(e.target.value.toUpperCase().slice(0, 6))}
                  className="bg-[#000e2a]/80 text-white placeholder-[#8892b0] border-[rgba(251,221,104,0.2)] font-mono text-lg tracking-widest uppercase w-36" maxLength={6} />
                <Button data-testid="owner-link-program-button" type="submit" disabled={linkLoading || linkCode.length !== 6}
                  className="bg-[#fbdd68] text-[#000e2a] font-bold text-xs uppercase tracking-wider hover:bg-[#ffe685]">
                  <Link2 className="w-4 h-4 mr-1.5" />{linkLoading ? 'Linking...' : 'Link'}
                </Button>
              </form>
              <div className="space-y-2">
                {activePrograms.map((p) => (
                  <div key={p.program_id} data-testid={`program-row-${p.program_id}`} className="flex items-center justify-between p-3 rounded-lg bg-[rgba(20,27,80,0.4)] border border-[rgba(251,221,104,0.06)]">
                    <div className="flex items-center gap-3">
                      <Key className="w-4 h-4 text-[#fbdd68]" />
                      <span className="font-mono text-white text-sm tracking-wider">{p.code}</span>
                      <span className="text-xs text-[#8892b0]">{p.linked_at ? new Date(p.linked_at).toLocaleDateString() : ''}</span>
                    </div>
                    <Button data-testid={`unlink-program-${p.program_id}`} onClick={() => handleUnlink(p.program_id)} variant="ghost" size="sm"
                      className="text-[#ef4444] hover:text-[#ef4444] hover:bg-[#ef4444]/10"><Unlink className="w-4 h-4" /></Button>
                  </div>
                ))}
                {activePrograms.length === 0 && <p className="text-center text-[#8892b0] py-6">No programs linked yet</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hosts */}
        <TabsContent value="hosts">
          <Card className="glass-panel rounded-2xl border-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white text-lg">Hosts</CardTitle>
                  <CardDescription className="text-[#8892b0] mt-1">
                    Everyone on your schedule — same list as the Employees tab in Schedule Admin. Adding a host here gives them a cloud login.
                  </CardDescription>
                </div>
                <Dialog open={addHostOpen} onOpenChange={setAddHostOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="owner-add-host-button" className="bg-[#fbdd68] text-[#000e2a] font-bold text-xs uppercase tracking-wider hover:bg-[#ffe685]">
                      <UserPlus className="w-4 h-4 mr-1.5" /> Add Host
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#141b50] border-[rgba(251,221,104,0.15)] text-white">
                    <DialogHeader><DialogTitle className="text-white">Add New Host</DialogTitle></DialogHeader>
                    <form onSubmit={handleAddHost} className="space-y-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-[#8892b0]">Name</Label>
                        <Input data-testid="add-host-name-input" value={hostForm.name} onChange={(e) => setHostForm(p => ({ ...p, name: e.target.value }))}
                          className="bg-[#000e2a]/80 text-white border-[rgba(251,221,104,0.2)]" required />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-[#8892b0]">Email</Label>
                        <Input data-testid="add-host-email-input" type="email" value={hostForm.email} onChange={(e) => setHostForm(p => ({ ...p, email: e.target.value }))}
                          className="bg-[#000e2a]/80 text-white border-[rgba(251,221,104,0.2)]" required />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-[#8892b0]">Temporary Password</Label>
                        <Input data-testid="add-host-password-input" type="text" value={hostForm.password} onChange={(e) => setHostForm(p => ({ ...p, password: e.target.value }))}
                          className="bg-[#000e2a]/80 text-white border-[rgba(251,221,104,0.2)]" required />
                        <p className="text-xs text-[#8892b0]">The host will change this on first login</p>
                      </div>
                      <Button data-testid="add-host-submit-button" type="submit" disabled={hostLoading}
                        className="w-full bg-[#fbdd68] text-[#000e2a] font-bold uppercase tracking-wider hover:bg-[#ffe685]">
                        {hostLoading ? 'Adding...' : 'Add Host'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {hosts.map((h) => (
                  <div key={h.id} data-testid={`host-row-${h.id}`} className="flex items-center justify-between p-3 rounded-lg bg-[rgba(20,27,80,0.4)] border border-[rgba(251,221,104,0.06)]">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#fbdd68]/15 flex items-center justify-center">
                        <span className="text-xs font-bold text-[#fbdd68]">{h.name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{h.name}</p>
                        <p className="text-xs text-[#8892b0]">{h.email || 'No email'}</p>
                      </div>
                      <div className="flex gap-1.5">
                        {h.is_owner && <Badge className="bg-[#fbdd68]/15 text-[#fbdd68] border-[#fbdd68]/30 text-[10px]"><Crown className="w-3 h-3 mr-1" />Owner</Badge>}
                        {h.linked && !h.is_owner && <Badge className="bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30 text-[10px]">Cloud login</Badge>}
                        {!h.linked && <Badge className="bg-transparent text-[#8892b0] border-[#8892b0]/30 text-[10px]">No login yet</Badge>}
                        {h.active === false && <Badge className="bg-[#ef4444]/10 text-[#ef4444] border-[#ef4444]/30 text-[10px]">Inactive</Badge>}
                      </div>
                    </div>
                    {!h.is_owner && h.login_role !== 'master_admin' && (
                      <Button data-testid={`remove-host-${h.id}`} onClick={() => handleRemoveHost(h)} variant="ghost" size="sm"
                        className="text-[#ef4444] hover:text-[#ef4444] hover:bg-[#ef4444]/10"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                ))}
                {hosts.length === 0 && <p className="text-center text-[#8892b0] py-6">No hosts added yet</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   HOST DASHBOARD
   ════════════════════════════════════════════════════════════════════ */
function HostDashboard() {
  const { user } = useAuth();
  return (
    <div>
      <div className="mb-8">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#fbdd68]/80 mb-1.5">Command Center</p>
        <h1 data-testid="host-dashboard-title" className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
          Welcome, {user?.name}
        </h1>
        <p className="text-sm text-[#8892b0] mt-1">Host Dashboard</p>
      </div>
      <p className="text-sm text-[#8892b0]">Open Schedule in the sidebar to see and claim events.</p>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   MAIN ROUTER
   ════════════════════════════════════════════════════════════════════ */
export default function DashboardPage() {
  const { user } = useAuth();
  if (user?.role === 'master_admin') return <AdminDashboard />;
  if (user?.role === 'account_owner') return <OwnerDashboard />;
  return <HostDashboard />;
}
